import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from 'react-query'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { fetchAllAnalysis } from 'src/api/analysis/list'
import { EXCLUDED_TUMOUR_DNA_TEXT, analysisKind, linkCandidatesForTumour } from './linkCandidates'

// Search the organisation's own analyses by sample name (roadmap R1.2a): picks
// other analyses of the same patient. The list endpoint is organisation-scoped
// and the server checks every picked id again, so another organisation's
// analysis can never be linked.
//
// Every caller links FROM a somatic tumour DNA analysis, so by default the
// candidates are filtered to the analyses such a link is for (a germline or
// matched-normal analysis, an RNA analysis of the tumour) and grouped with the
// reason; other tumour DNA analyses are left out and the picker says why.
// `kinds` (with forTumour false) keeps only the analyses of those kinds
// (linkCandidates.analysisKind), for a picker of somatic analyses.

export const analysisOptionLabel = (option) => {
  if (!option) return ''
  const sample = option.display_sample_name || option.sample_name
  return sample ? `${sample} (${option.analysis_name})` : option.analysis_name || `Analysis ${option.analysis_id}`
}

const AnalysisSearchSelect = ({
  value,
  onChange,
  multiple,
  excludeIds,
  label,
  helperText,
  disabled,
  forTumour,
  kinds,
}) => {
  const [inputValue, setInputValue] = useState('')
  const query = inputValue.trim()

  const { data, isFetching } = useQuery(
    ['analysis-link-search', query],
    () => fetchAllAnalysis({ page: 0, limit: 20, sample_name: query }),
    { staleTime: 60 * 1000, keepPreviousData: true },
  )

  const { options, excludedTumourDna } = useMemo(() => {
    const excluded = new Set((excludeIds || []).map(String))
    const rows = (data?.results || []).filter((row) => !excluded.has(String(row.analysis_id)))
    if (!forTumour) {
      const kept = kinds ? rows.filter((row) => kinds.includes(analysisKind(row))) : rows
      return { options: kept, excludedTumourDna: 0 }
    }
    const result = linkCandidatesForTumour(rows)
    return { options: result.offered, excludedTumourDna: result.excludedTumourDna }
  }, [data, excludeIds, forTumour, kinds])

  const helper =
    excludedTumourDna > 0
      ? [helperText, `${excludedTumourDna} not offered. ${EXCLUDED_TUMOUR_DNA_TEXT}`].filter(Boolean).join(' ')
      : helperText

  return (
    <Autocomplete
      multiple={multiple}
      disabled={disabled}
      value={value}
      onChange={(_e, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_e, next, reason) => {
        // Keep the typed text when an option is picked in multiple mode.
        if (reason !== 'reset') setInputValue(next)
      }}
      options={options}
      loading={isFetching}
      filterOptions={(x) => x}
      groupBy={forTumour ? (option) => option.linkKind?.group || '' : undefined}
      getOptionLabel={analysisOptionLabel}
      isOptionEqualToValue={(option, val) => option?.analysis_id === val?.analysis_id}
      noOptionsText={query ? 'No matching analysis' : 'Type a sample name'}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.analysis_id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.display_sample_name || option.sample_name || option.analysis_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {[option.analysis_name, (option.tags || []).join(', ')].filter(Boolean).join(' | ')}
            </Typography>
            {forTumour && option.linkKind?.why && (
              <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ fontStyle: 'italic' }}>
                {option.linkKind.why}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          helperText={helper}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

AnalysisSearchSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  excludeIds: PropTypes.array,
  label: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  // Link candidates for a somatic tumour DNA analysis (the only caller today).
  forTumour: PropTypes.bool,
  // The analysis kinds offered when forTumour is false; all when absent.
  kinds: PropTypes.arrayOf(PropTypes.string),
}

AnalysisSearchSelect.defaultProps = {
  value: null,
  multiple: false,
  excludeIds: [],
  label: 'Analysis',
  helperText: undefined,
  disabled: false,
  forTumour: true,
  kinds: undefined,
}

export default AnalysisSearchSelect
